import { ChangeDetectorRef, Component, DestroyRef, ViewChild, inject } from '@angular/core';
import { PDFExportComponent } from '@progress/kendo-angular-pdf-export';
import { exportPDF } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';
import { firstValueFrom, forkJoin } from 'rxjs';
import { SharedService } from '@shared/services/shared.service';
import { AddressService } from 'app/services/address/address.service';
import { EntityOwnerService } from 'app/services/license/in-home-care/entity-owner.service';
import { OnCampusBuildingService } from 'app/services/on-campus-building/on-campus-building.service';
import { SatelliteLocationService } from 'app/services/satellite-location/satellite-location.service';
import { HospitalServiceLocationService } from 'app/services/license/hospital/hospital-service-location.service';
import { DocumentService } from 'app/services/document/document.service';
import { IHospitalLicense } from 'app/models/licenses/hospital/hospital-license';

export interface SnapshotSection {
  title: string;
  rows: { label: string; value: string }[];
}

const LABELS: Record<string, string> = {
  id: 'Application ID', licenseId: 'License number', isRenewal: 'Renewal application',
  isSicf: 'Special Inpatient Care Facility (SICF)', entityName: 'Hospital legal name',
  hospitalDBAName: 'Hospital DBA (Doing Business As) name', deemed: 'Deemed status',
  mainHospitalOutOfState: 'Main hospital located outside Oregon',
  inpatientBeds: 'Total on-campus inpatient beds',
  inpatientPsychiatricBeds: 'Total on-campus inpatient psychiatric beds',
  satelliteInpatientPsychiatricBeds: 'Total satellite inpatient psychiatric beds',
  adminPrintTitle: 'Administrator printed title', taxId: 'Tax ID', zip: 'ZIP code', firstName: 'Owner name',
  nurseManager: 'Nurse manager co-chair',
  directCare: 'Direct care co-chair',
  hospitalAdmin: 'Hospital administrator', chiefNurse: 'Chief nursing officer',
  filer: 'Application filer', emergencyContact: 'Emergency contact',
  emergencyDepartmentDirector: 'Emergency department director',
  technicalManagerCoChair: 'Technical manager co-chair',
  technicalStaffCoChair: 'Technical staffing committee staff co-chair',
  serviceStaffingCommitteeManagerCoChair: 'Service staffing committee manager co-chair',
  serviceStaffingCommitteeStaffCoChair: 'Service staffing committee staff co-chair'
};

export function snapshotSection(title: string, data: object, keys = Object.keys(data)): SnapshotSection {
  const record = data as Record<string, unknown>;
  return { title, rows: keys.map(key => {
    let value = record[key];
    // Service location types are stored as JSON arrays; render them as a column.
    if (typeof value === 'string' && value.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) { value = parsed; }
      } catch {
        // Preserve entered text when it is not a valid JSON list.
      }
    }
    if (Array.isArray(value)) {
      value = value.map(entry => String(entry)).join('\n');
    }
    if (/(phone|fax)$/i.test(key) && typeof value === 'string') {
      const phone = value.trim().match(/^\(?(\d{3})\)?[ .-]?(\d{3})[ .-]?(\d{4})(\s*(?:ext\.?|x|#).*)?$/i);
      if (phone) { value = `${phone[1]}-${phone[2]}-${phone[3]}${phone[4] || ''}`; }
    }
    let label = LABELS[key];
    if (!label) {
      const prefix = Object.keys(LABELS).find(p => key.startsWith(p) && /^(Name|Title|Email|Phone)$/.test(key.slice(p.length)));
      label = prefix ? `${LABELS[prefix]} — ${key.slice(prefix.length).toLowerCase()}` :
        key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());
    }
    const date = typeof value === 'string' && /Date$/.test(key) && value ? new Date(value) : null;
    return { label, value: value === null || value === undefined || value === '' ? 'Not provided' :
      typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
      date && !isNaN(date.getTime()) ? date.toLocaleDateString('en-US', { timeZone: 'UTC' }) :
      value instanceof Date ? value.toLocaleDateString('en-US') : String(value) };
  }) };
}

@Component({
  selector: 'app-hospital-snapshot',
  templateUrl: './hospital-snapshot.component.html',
  styleUrls: ['./hospital-snapshot.component.scss']
})
export class HospitalSnapshotComponent {
  @ViewChild('pdf') pdf!: PDFExportComponent;
  public exporting = false;
  public sections: SnapshotSection[] = [];
  public facilityName = '';
  public generatedAt = new Date();
  private readonly changes = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed = false;
  private readonly shared = inject(SharedService);
  private readonly addresses = inject(AddressService);
  private readonly owners = inject(EntityOwnerService);
  private readonly buildings = inject(OnCampusBuildingService);
  private readonly satellites = inject(SatelliteLocationService);
  private readonly services = inject(HospitalServiceLocationService);
  private readonly documents = inject(DocumentService);

  constructor() {
    this.destroyRef.onDestroy(() => { this.destroyed = true; });
  }

  async download(license: IHospitalLicense, formSections: SnapshotSection[]): Promise<void> {
    if (this.exporting) { return; }
    this.exporting = true;
    this.sections = [];
    this.facilityName = license.entityName || 'Hospital application';
    this.generatedAt = new Date();
    try {
      const type = this.shared.hospitalLicense;
      const related = await firstValueFrom(forkJoin({
        owners: this.owners.getEntityOwners(license.id, type, true),
        buildings: this.buildings.getBuildings(license.id, type, true),
        satellites: this.satellites.GetSatelliteLocations(license.id, type, true)
      }));
      const [main, owners, buildings, satellites] = await Promise.all([
        this.details(license.id, type, 'Hospital'),
        Promise.all(related.owners.map(async (owner, i) => [
          snapshotSection(`Owner ${i + 1}`, owner, ['firstName', 'lastName', 'phone', 'fax', 'taxId', 'contactWithClient', 'archived']),
          ...await this.addressSections(owner.id, this.shared.entityOwner, `Owner ${i + 1}`)
        ])),
        Promise.all(related.buildings.map(async (building, i) => [
          snapshotSection(`On-campus building ${i + 1}`, building, ['buildingId', 'buildingName', 'archived']),
          ...await this.details(building.id, 'OnCampusBuilding', `On-campus building ${i + 1}`, true)
        ])),
        Promise.all(related.satellites.map(async (satellite, i) => [
          snapshotSection(`Satellite location ${i + 1}`, satellite, ['satelliteId', 'buildingName', 'phone', 'archived']),
          ...await this.details(satellite.id, 'SatelliteLocation', `Satellite location ${i + 1}`, true)
        ]))
      ]);
      if (this.destroyed) { return; }
      this.sections = [
        snapshotSection('Application', license, ['id', 'licenseId', 'isRenewal', 'applicationDate', 'approvedDate', 'expirationDate']),
        ...formSections, ...main,
        ...(license.representativeContacts || []).map((contact, i) => snapshotSection(`Representative contact ${i + 1}`, contact,
          ['representativeType', 'representativeStaff', 'contactName', 'contactPhone', 'contactEmail'])),
        ...([] as SnapshotSection[]).concat(...owners, ...buildings, ...satellites)
      ];
      this.changes.detectChanges();
      await document.fonts.ready;
      if (this.destroyed) { return; }
      const group = await this.pdf.export();
      const data = await exportPDF(group, { multiPage: true, title: `${this.facilityName} - Application snapshot` });
      const name = `${this.facilityName}-${license.id}-application-snapshot`.replace(/[<>:"/\\|?*\x00-\x1f]/g, '-');
      saveAs(data, `${name}.pdf`);
    } catch (error) {
      this.shared.toastr.error('The application snapshot could not be exported. Please try again.');
    } finally {
      this.sections = [];
      this.exporting = false;
      if (!this.destroyed) { this.changes.markForCheck(); }
    }
  }

  private async addressSections(id: number, type: string, title: string): Promise<SnapshotSection[]> {
    const addresses = await firstValueFrom(this.addresses.getAddresses(id, type, true));
    return addresses.map((address, i) => snapshotSection(`${title} — address ${i + 1}`, {
      addressTypes: address.addressTypes.map(t => t.type),
      street: address.street, city: address.city,
      state: address.state, zip: address.zip, county: address.county, archived: address.archived
    }));
  }

  private async details(id: number, type: string, title: string, includeServices = false): Promise<SnapshotSection[]> {
    const [addresses, documents, services] = await Promise.all([
      this.addressSections(id, type, title),
      firstValueFrom(this.documents.getDocuments(id, type, true)),
      includeServices ? firstValueFrom(this.services.getHospitalServiceLocations(id, type, true)) : Promise.resolve([])
    ]);
    return [...addresses,
      ...services.map((service, i) => snapshotSection(`${title} — service ${i + 1}`, service,
        ['floor', 'suite', 'servicesProvided', 'type', 'archived'])),
      { title: `${title} — uploaded documents`, rows: documents.filter(d => !d.adminOnly).map(d => ({
        label: d.types.filter(t => !t.adminOnly).map(t => t.type).join('\n') || 'Document',
        value: `${d.name}${d.archived ? ' (Archived)' : ''}`
      })) }
    ];
  }
}
